/**
 * Weather location resolver.
 *
 * Resolution priority:
 *   1. user-saved preference  (localStorage `cc_weather_location`)
 *   2. browser geolocation    (if permission granted)
 *   3. IP-based approximation (ipapi.co — free, no key)
 *   4. London (hard fallback)
 *
 * Reverse-geocoding uses BigDataCloud's open client API (no key, generous
 * free tier, returns city + region + country). Forward search uses
 * Open-Meteo's geocoding API (same provider as the weather data).
 *
 * Everything is wrapped in graceful try/catch — if any provider misbehaves
 * we fall through to the next step rather than break the widget.
 */

const STORAGE_KEY = 'cc_weather_location';
const RECENTS_KEY = 'cc_weather_recents';

const LONDON = {
  name: 'London',
  latitude: 51.5074,
  longitude: -0.1278,
  region: 'England',
  country: 'United Kingdom',
  source: 'default',
};

// ----------------------------- persistence ----------------------------------

export const getSavedLocation = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.latitude === 'number' && typeof parsed.longitude === 'number') {
      return { ...parsed, source: 'saved' };
    }
    return null;
  } catch (_) {
    return null;
  }
};

export const saveLocation = (loc) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        name: loc.name,
        latitude: loc.latitude,
        longitude: loc.longitude,
        region: loc.region || null,
        country: loc.country || null,
      }),
    );
  } catch (_) { /* localStorage may be disabled — non-fatal */ }
};

export const clearSavedLocation = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* noop */ }
};

export const getRecentLocations = () => {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, 5) : [];
  } catch (_) {
    return [];
  }
};

export const pushRecentLocation = (loc) => {
  try {
    const current = getRecentLocations();
    const key = `${loc.latitude.toFixed(2)},${loc.longitude.toFixed(2)}`;
    const filtered = current.filter((c) => `${c.latitude.toFixed(2)},${c.longitude.toFixed(2)}` !== key);
    const next = [
      { name: loc.name, latitude: loc.latitude, longitude: loc.longitude, region: loc.region || null, country: loc.country || null },
      ...filtered,
    ].slice(0, 5);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch (_) { /* noop */ }
};

// ----------------------------- providers ------------------------------------

/** Browser geolocation wrapped as a promise with a hard timeout. */
export const requestBrowserGeolocation = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error('Geolocation not supported by this browser.'));
    return;
  }
  const onErr = (err) => reject(err);
  const onOk = (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
  navigator.geolocation.getCurrentPosition(onOk, onErr, {
    timeout: 8000,
    maximumAge: 5 * 60 * 1000, // 5min cache — avoids re-prompting on every refresh
    enableHighAccuracy: false,
  });
});

/** Reverse geocode lat/lon → human-readable place name.
 *  Two-tier fallback: BigDataCloud (CORS-friendly, JSON shape stable) →
 *  Nominatim (OpenStreetMap, used as a backstop when BDC returns no city). */
export const reverseGeocode = async (latitude, longitude) => {
  // Tier 1 — BigDataCloud
  try {
    const r = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
    );
    if (r.ok) {
      const d = await r.json();
      const name = d.city || d.locality || d.principalSubdivision;
      if (name) {
        return {
          name,
          region: d.principalSubdivision || null,
          country: d.countryName || null,
        };
      }
    }
  } catch (_) { /* fall through */ }

  // Tier 2 — Nominatim
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`,
      { headers: { 'Accept-Language': 'en' } },
    );
    if (r.ok) {
      const d = await r.json();
      const a = d.address || {};
      const name = a.city || a.town || a.village || a.hamlet || a.suburb || a.county || a.state;
      if (name) {
        return {
          name,
          region: a.state || a.county || null,
          country: a.country || null,
        };
      }
    }
  } catch (_) { /* fall through */ }

  return { name: 'Your location', region: null, country: null };
};

/** Forward-geocode user search input → up to N suggestions. */
export const searchPlaces = async (query) => {
  const q = (query || '').trim();
  if (q.length < 2) return [];
  try {
    const r = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=8&language=en&format=json`,
    );
    if (!r.ok) throw new Error(`geocode ${r.status}`);
    const d = await r.json();
    return (d.results || []).map((row) => ({
      id: `${row.id}`,
      name: row.name,
      latitude: row.latitude,
      longitude: row.longitude,
      region: row.admin1 || null,
      country: row.country || null,
    }));
  } catch (_) {
    return [];
  }
};

/** IP-based approximation — used only when GPS is unavailable or denied. */
export const ipGeolocate = async () => {
  try {
    const r = await fetch('https://ipapi.co/json/');
    if (!r.ok) throw new Error(`ipapi ${r.status}`);
    const d = await r.json();
    if (typeof d.latitude !== 'number' || typeof d.longitude !== 'number') return null;
    return {
      name: d.city || 'Approx. location',
      latitude: d.latitude,
      longitude: d.longitude,
      region: d.region || null,
      country: d.country_name || null,
      source: 'ip',
    };
  } catch (_) {
    return null;
  }
};

// ----------------------------- resolver -------------------------------------

/**
 * Resolve the best location given user preferences and runtime signals.
 *
 *   opts.preferGps  — when true, skip the saved preference (used by the
 *                     "Use My Location" button so the user can override
 *                     a saved location without clearing it manually).
 *
 * Always resolves to a usable location with a `source` field describing
 * how it was determined: 'gps' | 'saved' | 'ip' | 'default'.
 */
export const resolveLocation = async ({ preferGps = false } = {}) => {
  if (!preferGps) {
    const saved = getSavedLocation();
    if (saved) return saved;
  }
  try {
    const { latitude, longitude } = await requestBrowserGeolocation();
    const place = await reverseGeocode(latitude, longitude);
    return { latitude, longitude, ...place, source: 'gps' };
  } catch (_) {
    // GPS denied / timed out — try IP.
  }
  const ip = await ipGeolocate();
  if (ip) return ip;
  return LONDON;
};

export { LONDON };
